import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { User } from '@src/app/_models/user/user';
import { MessageService } from '@src/app/_services/message.service';
import { AuthenticationService } from '@src/app/_services/authentication.service';
import { NgForOf, NgIf, registerLocaleData } from '@angular/common';
import { DataService } from '@src/app/_services/data.service';
import { PermissionService } from '@src/app/_services/permission.service';

import moment from 'moment-business-days';
import localeCs from '@angular/common/locales/cs';
import { ButtonPropsModel, DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserInput, UsersService } from '@src/app/features/users/users.service';
import { distinctUntilChanged } from 'rxjs';
import { UserEditPasswordFormComponent } from '@src/app/features/users/_forms/user-edit-password-form/user-edit-password-form.component';
import { UserFormComponent } from '@src/app/features/users/_forms/user-form/user-form.component';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { LoaderComponent } from '@src/app/shared_features/components/loader/loader.component';

moment.updateLocale('cs', { workingWeekdays: [1, 2, 3, 4, 5] });
registerLocaleData(localeCs);

@UntilDestroy()
@Component({
    selector: 'app-users-detail',
    templateUrl: './users-detail.component.html',
    styleUrls: ['./users-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogModule,
        UserEditPasswordFormComponent,
        UserFormComponent,
        ButtonModule,
        LoaderComponent,
        RouterLink,
        NgForOf,
        NgIf,
    ],
})
export class UsersDetailComponent implements OnInit {
    // Variables
    currentUser: User | null;

    user: User;

    selectedFile: File;

    // Forms
    isCreate = false;

    isUpdate = false;

    isCopy = false;

    // Dialogs
    renderUserFormDialog = false;

    renderChangeUserPassword = false;

    buttonsDelete: ButtonPropsModel[] = [
        {
            click: (): void => {
                this.deAuthorize();
            },
            isFlat: false,
            buttonModel: {
                content: 'OK',
                cssClass: 'e-success e-outline',
            },
        },
        {
            click: (): void => {
                this.confirmationDelete.hide();
            },
            buttonModel: {
                content: 'ZRUŠIT',
                cssClass: 'e-danger e-outline',
            },
        },
    ];

    buttonsReset: ButtonPropsModel[] = [
        {
            click: (): void => {
                this.resetPassword();
            },
            isFlat: false,
            buttonModel: {
                content: 'OK',
                cssClass: 'e-success e-outline',
            },
        },
        {
            click: (): void => {
                this.confirmationReset.hide();
            },
            buttonModel: {
                content: 'ZRUŠIT',
                cssClass: 'e-danger e-outline',
            },
        },
    ];

    buttonsAuthorized: ButtonPropsModel[] = [
        {
            click: (): void => {
                this.reAuthorize();
            },
            isFlat: false,
            buttonModel: {
                content: 'OK',
                cssClass: 'e-success e-outline',
            },
        },
        {
            click: (): void => {
                this.confirmationAuthorized.hide();
            },
            buttonModel: {
                content: 'ZRUŠIT',
                cssClass: 'e-danger e-outline',
            },
        },
    ];

    // Loaders
    loadingUser = true;

    // Dialogs
    @ViewChild('confirmationDelete') confirmationDelete: DialogComponent;

    @ViewChild('confirmationReset') confirmationReset: DialogComponent;

    @ViewChild('confirmationAuthorized') confirmationAuthorized: DialogComponent;

    @ViewChild('passwordWarning') passwordWarningDialog: DialogComponent;

    @ViewChild('userPasswordForm') userPasswordFormDialog: DialogComponent;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly dataService: DataService,
        private readonly userService: UsersService,
        private readonly authenticationService: AuthenticationService,
        private readonly messages: MessageService,
        private readonly permissionService: PermissionService,
        private readonly ref: ChangeDetectorRef
    ) {
        this.currentUser = this.authenticationService.currentUserValue;
    }

    ngOnInit(): void {
        // LOCAL DATA
        this.route.params
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((params: Params) => {
                this.dataService.setUserDetailSource(parseInt(params.id as string, 10));
            });

        // PSWD WARN
        if (
            this.currentUser?.password === '54460754b2d445cae91ea24363cc1085b40b95be' &&
            this.user.id === this.currentUser.id
        ) {
            this.passwordWarningDialog.show();
        }

        this.dataService.userDetailSource.pipe(untilDestroyed(this)).subscribe({
            next: (user: User | undefined) => {
                if (user) {
                    this.user = user;
                    this.loadingUser = false;
                    this.ref.markForCheck();
                }
            },
            error: error => {
                console.error(error);
            },
        });
    }

    onFileChanged(event: unknown): void {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedFile = event.target.files[0] as File;
    }

    editUser(): void {
        if (
            this.currentUser &&
            (this.permissionService.checkUserISAdministrative(this.currentUser) ||
                this.user.id === this.currentUser.id)
        ) {
            this.isCopy = false;
            this.isCreate = false;
            this.isUpdate = true;
            this.renderUserFormDialog = true;
            this.ref.markForCheck();
        } else {
            const body = 'Nemáte oprávnění provést tuto akci...';
            const options = { progressBar: true, timeOut: 5000 };

            this.messages.showError('Nedostatečné oprávnění', body, options);
            this.loadingUser = false;
            this.ref.markForCheck();
        }
    }

    changePassword(): void {
        if (this.currentUser?.id === this.user.id) {
            this.renderChangeUserPassword = true;
            this.ref.markForCheck();
        } else {
            const body = 'heslo patří jinému uživateli';
            const options = { progressBar: true, timeOut: 5000, toastClass: 'red' };

            this.messages.showError('Toto heslo nelze změnit', body, options);
            this.loadingUser = false;
            this.ref.markForCheck();
        }
    }

    deAuthorize(): void {
        if (
            this.currentUser &&
            this.permissionService.checkUserISAdministrative(this.currentUser)
        ) {
            this.loadingUser = true;
            this.user.deleted_by = this.currentUser.id;
            this.userService
                .deleteUser(this.user)
                ?.pipe(untilDestroyed(this))
                .subscribe(
                    (user: User) => {
                        const body = 'uživatele: #' + user.fullname;
                        const options = {
                            progressBar: true,
                            timeOut: 5000,
                            toastClass: 'success',
                        };

                        this.user.authorized = false;
                        this.messages.showSuccess('Úspěšná deuatorizace', body, options);
                        this.dataService.setUserDetailSource(this.user.id);
                        this.confirmationDelete.hide();
                        this.ref.markForCheck();
                    },
                    error => {
                        console.error(error);
                        this.loadingUser = false;
                        this.confirmationDelete.hide();
                        this.ref.markForCheck();
                    }
                );
        } else {
            const body = 'Nemáte oprávnění provést tuto akci...';
            const options = { progressBar: true, timeOut: 5000 };

            this.messages.showError('Nedostatečné oprávnění', body, options);
            this.loadingUser = false;
            this.ref.markForCheck();
        }
    }

    reAuthorize(): void {
        if (
            this.currentUser &&
            this.permissionService.checkUserISAdministrative(this.currentUser)
        ) {
            this.loadingUser = true;
            this.user.password = 'j0n2019System';
            this.userService
                .updateUser(this.user as UserInput, true)
                ?.pipe(untilDestroyed(this))
                .subscribe({
                    next: (user: User) => {
                        const body = `uživatele: #${user.id}`;
                        const options = { progressBar: true, timeOut: 5000 };

                        this.messages.showSuccess('Úspěšná úprava', body, options);
                        this.dataService.setUserDetailSource(this.user.id);
                        this.confirmationAuthorized.hide();
                        this.ref.markForCheck();
                    },
                    error: error => {
                        console.error(error);
                        this.loadingUser = false;
                        this.confirmationAuthorized.hide();
                        this.ref.markForCheck();
                    },
                });
        } else {
            const body = 'Nemáte oprávnění provést tuto akci...';
            const options = { progressBar: true, timeOut: 5000 };

            this.messages.showError('Nedostatečné oprávnění', body, options);
            this.loadingUser = false;
            this.confirmationAuthorized.hide();
            this.ref.markForCheck();
        }
    }

    resetPassword(): void {
        if (
            this.currentUser &&
            this.permissionService.checkUserISAdministrative(this.currentUser)
        ) {
            this.loadingUser = true;
            this.user.password = 'j0n2019System';
            this.userService
                .updateUser(this.user as UserInput)
                ?.pipe(untilDestroyed(this))
                .subscribe({
                    next: (user: User) => {
                        const body = `uživatele: #${user.fullname}`;
                        const options = {
                            progressBar: true,
                            timeOut: 5000,
                            toastClass: 'info',
                        };

                        this.messages.showSuccess('Úspěšný reset hesla', body, options);
                        this.dataService.setUserDetailSource(this.user.id);
                        this.confirmationReset.hide();
                        this.ref.markForCheck();
                    },
                    error: error => {
                        console.error(error);
                        this.loadingUser = false;
                        this.confirmationReset.hide();
                        this.ref.markForCheck();
                    },
                });
        } else {
            const body = 'Nemáte oprávnění provést tuto akci...';
            const options = { progressBar: true, timeOut: 5000 };

            this.messages.showError('Nedostatečné oprávnění', body, options);
            this.loadingUser = false;
            this.confirmationReset.hide();
            this.ref.markForCheck();
        }
    }
}
